import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { forceSignOut, isFatalAuthError } from '@/lib/forceSignOut';
import type { User } from '@supabase/supabase-js';
import type { Enums } from '@/integrations/supabase/types';
import type { SystemRole, TradeRole } from '@/types/index';


type AppRole = Enums<'app_role'>;

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  avatar_url: string | null;
  role: AppRole | null; // legacy — kept for backward compat
  systemRole: SystemRole | null;
  tradeRole: TradeRole | null;
  company_id: string | null;
  companyIds: string[];
  isPlatformOwner: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  authUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authUserRef = useRef<User | null>(null);

  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);

  const fetchProfile = useCallback(async (userId: string, email: string, overrideCompanyId?: string) => {
    try {
      // Fetch profile, role, company, trade role in parallel
      const [profileRes, roleRes, companyRes, tradeRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
        supabase.from('user_roles').select('role, system_role').eq('user_id', userId).maybeSingle(),
        supabase.from('company_users').select('company_id').eq('user_id', userId).eq('is_active', true).order('created_at', { ascending: true }),
        supabase.from('user_trade_roles').select('trade_role').eq('user_id', userId).maybeSingle(),
      ]);

      const profile = profileRes.data;
      const roleData = roleRes.data;
      const companyUsers = companyRes.data || [];
      const tradeData = tradeRes.data;

      const companyIds = companyUsers.map((row: any) => row.company_id).filter(Boolean);
      const homeCompanyId = companyIds[0] || null;
      const storedCompanyId = sessionStorage.getItem('platform_active_company');
      const requestedCompanyId = overrideCompanyId || storedCompanyId;
      const activeCompanyId = requestedCompanyId && companyIds.includes(requestedCompanyId)
        ? requestedCompanyId
        : homeCompanyId;
      if (storedCompanyId && storedCompanyId !== activeCompanyId) {
        sessionStorage.removeItem('platform_active_company');
      }

      // Check if user's home company is a platform owner
      let isPlatformOwner = false;
      if (homeCompanyId) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('is_platform_owner')
          .eq('id', homeCompanyId)
          .maybeSingle();
        isPlatformOwner = !!(companyData as any)?.is_platform_owner;
      }

      setUser({
        id: userId,
        email: profile?.email || email,
        full_name: profile?.full_name || email,
        phone: profile?.phone || null,
        avatar_url: profile?.avatar_url || null,
        role: (roleData?.role as AppRole) || null,
        systemRole: (roleData?.system_role as SystemRole) || null,
        tradeRole: (tradeData?.trade_role as TradeRole) || null,
        company_id: activeCompanyId,
        companyIds,
        isPlatformOwner,
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Listener for ONGOING auth changes (does NOT control isLoading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        // Proactive session expiry handling — redirect before errors occur
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          setAuthUser(null);
          setUser(null);
          // Only redirect if not already on login/public pages
          const path = window.location.pathname;
          if (path !== '/login' && path !== '/' && !path.startsWith('/portal') && !path.startsWith('/feedback') && !path.startsWith('/share') && !path.startsWith('/reset-password')) {
            window.location.href = '/login';
          }
          return;
        }

        setAuthUser(session?.user ?? null);

        if (session?.user) {
          // Defer to avoid Supabase auth deadlock
          setTimeout(() => {
            if (isMounted) {
              fetchProfile(session.user.id, session.user.email || '');
            }
          }, 0);
        } else {
          setUser(null);
        }
      }
    );

    // INITIAL load — validate token against server, not just localStorage
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (session?.user) {
          // Re-validate the cached token by calling the auth server.
          // getSession() only reads localStorage — a malformed/stale token
          // (e.g. from a previous signing key) still parses as a "session"
          // but every subsequent request will 401. getUser() catches this.
          const { data: userData, error: userErr } = await supabase.auth.getUser();
          if (userErr || !userData?.user) {
            if (isFatalAuthError(userErr) || !userData?.user) {
              await forceSignOut('token_invalid');
              return;
            }
          }
          setAuthUser(session.user);
          await fetchProfile(session.user.id, session.user.email || '');
        } else {
          setAuthUser(null);
        }
      } catch (err) {
        if (isFatalAuthError(err)) {
          await forceSignOut('token_invalid');
          return;
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    // Periodic session health check — every 5 minutes.
    // Uses getUser() (network call) so malformed/revoked tokens are caught,
    // not just missing ones.
    const SESSION_CHECK_INTERVAL = 5 * 60 * 1000;
    const healthCheck = setInterval(async () => {
      if (!isMounted || !authUserRef.current) return;
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error || !data?.user) {
          if (isFatalAuthError(error) || !data?.user) {
            await forceSignOut('token_invalid');
          }
        }
      } catch (err) {
        if (isFatalAuthError(err)) {
          await forceSignOut('token_invalid');
        }
        // Otherwise: network error — let next tick retry
      }
    }, SESSION_CHECK_INTERVAL);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearInterval(healthCheck);
    };
  }, [fetchProfile]);


  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  }, []);

  const logout = useCallback(async () => {
    // forceSignOut handles localStorage cleanup, signOut, and hard redirect
    await forceSignOut('manual');
  }, []);


  const switchCompany = useCallback(async (companyId: string) => {
    sessionStorage.setItem('platform_active_company', companyId);
    sessionStorage.removeItem('app_mode');
    if (authUser) {
      await fetchProfile(authUser.id, authUser.email || '', companyId);
    }
  }, [authUser, fetchProfile]);

  return (
    <AuthContext.Provider value={{ user, authUser, isAuthenticated: !!authUser, isLoading, login, logout, switchCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
